import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { fmtError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { auditRecipientText } from "@/lib/emailAudit";
import { Send, Mail, Pencil, ImagePlus, X, Monitor, Laptop, Smartphone, Copy, Link as LinkIcon, AlertTriangle, Pause, Play, Square, RotateCcw } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import OutlookReportDialog from "./OutlookReportDialog";

// Microsoft Outlook 365 bulk marketing — app-only Graph sends, no OAuth
// connect step. Sender mailbox is editable right here (CRM settings).
export default function OutlookMarketingCard() {
  const [status, setStatus] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [testError, setTestError] = useState("");
  const [busy, setBusy] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [form, setForm] = useState({ subject: "", html: "", recipients: "" });
  const [batches, setBatches] = useState([]);
  const [editingSender, setEditingSender] = useState(false);
  const [senderDraft, setSenderDraft] = useState("");
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [cats, setCats] = useState([]);
  const [selCats, setSelCats] = useState([]);
  // Contact Source — strictly ONE source at a time (18.docx): the two
  // databases never merge in a mass-email audience.
  const [contactSource, setContactSource] = useState("marketing");
  const [importingCrm, setImportingCrm] = useState(false);
  const [msgTab, setMsgTab] = useState("code");
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const [logo, setLogo] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [reportBatch, setReportBatch] = useState(null);
  const logoRef = useRef(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [{ data: s }, { data: b }] = await Promise.all([
        api.get("/marketing/outlook/status"),
        api.get("/marketing/outlook/batches", { params: { limit: 5 } }),
      ]);
      setStatus(s);
      setBatches(b || []);
    } catch { setStatus({ configured: false }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!batches.some((b) => b.status === "running")) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [batches, load]);

  // 27.docx — pause / resume / stop / retry-failed per batch.
  const batchAction = async (b, action) => {
    if (action === "cancel" && !window.confirm(`Stop "${b.subject}" permanently?\n\n${Math.max(0, (b.total || 0) - (b.sent || 0) - (b.failed || 0))} pending recipients will NOT receive the email. This cannot be resumed.`)) return;
    try {
      const { data } = await api.post(`/marketing/outlook/batches/${b.id}/${action}`);
      if (action === "resume") toast.success(data.resuming_from ? `Resuming from recipient #${data.resuming_from.toLocaleString()}…` : "Batch already complete");
      else if (action === "pause") toast.success("Campaign paused — progress saved. Resume any time.");
      else if (action === "cancel") toast.success("Campaign stopped permanently");
      else if (action === "retry-failed") toast.success(`Retrying ${data.retrying.toLocaleString()} failed email${data.retrying === 1 ? "" : "s"}…`);
      load();
    } catch (e) { toast.error(fmtError(e)); }
  };

  const saveSender = async () => {
    setBusy(true);
    try {
      await api.post("/marketing/outlook/sender", { email: senderDraft.trim() });
      toast.success(`Sender mailbox updated to ${senderDraft.trim()}`);
      setEditingSender(false);
      load();
    } catch (e) { toast.error(fmtError(e)); } finally { setBusy(false); }
  };

  const testSend = async () => {
    setBusy(true);
    setTestError("");
    try {
      const { data } = await api.post("/marketing/outlook/test-send", { to: testTo });
      toast.success(`Test email sent to ${testTo} from ${data.from} — check the inbox!`, { duration: 6000 });
      setTestTo(""); load();
    } catch (e) {
      const msg = String(fmtError(e) || "Send failed");
      setTestError(msg);
      toast.error(msg, { duration: 8000 });
    } finally { setBusy(false); }
  };

  useEffect(() => {
    if (!composeOpen) return;
    api.get("/marketing-contact-categories").then(({ data }) => setCats(data || [])).catch(() => setCats([]));
    api.get("/marketing/outlook/logo").then(({ data }) => setLogo(data?.url ? data : null)).catch(() => {});
  }, [composeOpen]);

  const toggleCat = (name) => setSelCats((s) => (s.includes(name) ? s.filter((x) => x !== name) : [...s, name]));

  const loadContactEmails = async () => {
    try {
      let contacts = [];
      if (contactSource === "crm") {
        // CRM Contacts ONLY — never mixes in marketing/leads/clients (18.docx).
        const { data } = await api.get("/contacts");
        contacts = data || [];
      } else if (selCats.length > 0) {
        const results = await Promise.all(selCats.map((c) => api.get("/marketing-contacts", { params: { category: c } })));
        contacts = results.flatMap((r) => r.data || []);
      } else {
        const { data } = await api.get("/marketing-contacts");
        contacts = data || [];
      }
      // Raw list (22.docx): duplicates/invalids stay visible so the stats bar
      // can report Total / Unique / Duplicates / Invalid honestly.
      const emails = contacts.map((c) => (c.email || "").trim().toLowerCase()).filter(Boolean);
      const srcLabel = contactSource === "crm" ? "CRM Contacts" : (selCats.length ? selCats.join(", ") : "all Marketing Contacts");
      if (!emails.length) { toast.info(`No contacts with email addresses found in ${srcLabel}`); return; }
      setForm((f) => ({ ...f, recipients: emails.join("\n") }));
      toast.success(`${emails.length} email addresses loaded from ${srcLabel}`);
    } catch (e) { toast.error(fmtError(e)); }
  };

  // 22/24.docx — live recipient audit, recomputed on every change to the box
  // (Load all, category switches, manual paste, add/remove). Each count is
  // clickable and opens the actual email list.
  const recipientStats = useMemo(() => auditRecipientText(form.recipients), [form.recipients]);
  const [statDetail, setStatDetail] = useState(null); // total | unique | duplicates | invalid

  const importCrmToMarketing = async () => {
    if (!window.confirm("Copy all CRM contacts into Marketing Contacts? Originals stay in CRM; duplicates are skipped.")) return;
    setImportingCrm(true);
    try {
      const { data } = await api.post("/marketing-contacts/import-from-crm", {});
      toast.success(`Imported ${data.inserted} CRM contact${data.inserted === 1 ? "" : "s"} into Marketing Contacts · ${data.skipped} skipped (duplicates)`);
    } catch (e) { toast.error(fmtError(e)); } finally { setImportingCrm(false); }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/marketing/outlook/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImages((imgs) => [...imgs, data.url]);
      toast.success("Image attached — it will appear inside the email");
    } catch (e) { toast.error(fmtError(e)); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  // Img tags whose src email clients cannot load (data:, cid:, relative
  // paths) — the top cause of "logo not showing" in real inboxes.
  const badImgSrcs = [...form.html.matchAll(/<img[^>]*\ssrc\s*=\s*["']([^"']+)["']/gi)]
    .map((m) => m[1])
    .filter((src) => !/^https?:\/\//i.test(src));

  // Public URLs that browsers render but Outlook desktop / some inboxes
  // won't: SVG and WebP files (why "preview shows it, inbox doesn't").
  const riskyImgSrcs = [...form.html.matchAll(/<img[^>]*\ssrc\s*=\s*["']([^"']+)["']/gi)]
    .map((m) => m[1])
    .filter((src) => /^https?:\/\//i.test(src) && /\.(svg|webp)(\?|#|$)/i.test(src));

  const copyImageUrl = async (u) => {
    try {
      await navigator.clipboard.writeText(u);
      toast.success("Image URL copied — paste it into your HTML <img src=\"…\">");
    } catch {
      window.prompt("Copy this image URL:", u);
    }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/marketing/outlook/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogo(data);
      toast.success("Company logo saved — it appears at the top of every email");
    } catch (e) { toast.error(fmtError(e)); } finally { setLogoBusy(false); if (logoRef.current) logoRef.current.value = ""; }
  };

  const removeLogo = async () => {
    try {
      await api.delete("/marketing/outlook/logo");
      setLogo(null);
      toast.success("Logo removed");
    } catch (e) { toast.error(fmtError(e)); }
  };

  const buildHtml = () => {
    let html = form.html;
    // Full HTML pastes (tags present) are sent as-is; plain text gets a
    // 600px responsive wrapper. Preview uses the exact same transformation
    // as the actual sent email (12.docx).
    if (!/<[a-z][^>]*>/i.test(html)) {
      html = `<div style="max-width:600px;margin:0 auto;font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:1.6;color:#1a1a1a">${html.replace(/\n/g, "<br/>")}</div>`;
    }
    if (images.length) {
      // width attr + display:block + height:auto keeps images visible and
      // scaled on mobile email clients (Gmail / Outlook mobile).
      html += `<div style="max-width:600px;margin:0 auto">` + images.map((u) => `<div style="margin-top:16px"><img src="${u}" alt="" width="600" style="display:block;width:100%;max-width:600px;height:auto;border-radius:8px"/></div>`).join("") + `</div>`;
    }
    if (logo?.url) {
      // Company logo header — responsive on desktop/laptop/mobile (13.docx).
      html = `<div style="max-width:600px;margin:0 auto;padding:18px 0 14px;text-align:center;background:#ffffff"><img src="${logo.url}" alt="Company logo" width="180" style="display:inline-block;width:180px;max-width:60%;height:auto"/></div>` + html;
    }
    return html;
  };

  const sendBulk = async () => {
    // Send exactly the "Will Send" list: valid + de-duped (22.docx).
    const recipients = recipientStats.unique;
    if (!recipients.length) { toast.error("Add at least one valid recipient email"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/marketing/outlook/send-bulk", {
        subject: form.subject, html: buildHtml(), recipients,
      });
      toast.success(`Bulk send started — ${data.total} emails queued${data.batches > 1 ? ` in ${data.batches} batches` : ""}${data.unsubscribed_skipped ? ` · ${data.unsubscribed_skipped} unsubscribed contact${data.unsubscribed_skipped === 1 ? "" : "s"} skipped` : ""}`);
      setComposeOpen(false);
      setForm({ subject: "", html: "", recipients: "" });
      setImages([]);
      load();
    } catch (e) { toast.error(fmtError(e)); } finally { setBusy(false); }
  };

  if (!status) return <Skeleton className="h-32 rounded-3xl" />;

  return (
    <Card className="rounded-3xl border-sky-200/70 shadow-soft" data-testid="outlook-marketing-card">
      <CardContent className="p-4 sm:p-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="h-10 w-10 rounded-2xl bg-sky-50 text-sky-700 grid place-items-center"><Mail className="h-4 w-4" /></div>
          <div className="flex-1 min-w-0">
            <p className="font-display text-base font-extrabold">Microsoft Outlook 365 — Bulk Marketing Email</p>
            <p className="text-xs text-muted-foreground truncate">
              {status.connected
                ? <>Sends from <strong>{status.sender}</strong> · {status.sent_today}/{status.daily_limit} sent today</>
                : status.configured ? "Configured — set the sender mailbox below" : "Server credentials missing (MS_CLIENT_ID / MS_TENANT_ID / MS_CLIENT_SECRET)"}
            </p>
          </div>
          <Badge variant="outline" className={`text-[10px] rounded-full ${status.connected ? "border-sky-200 bg-sky-50 text-sky-700" : "border-border"}`} data-testid="outlook-status-badge">
            {status.connected ? "Live" : "Not ready"}
          </Badge>
        </div>

        {/* Sender mailbox — configurable from CRM settings, no code change */}
        <div className="mt-3 p-3 rounded-2xl bg-muted/30 border border-border/50">
          {editingSender ? (
            <div className="flex flex-wrap items-center gap-2">
              <Input value={senderDraft} onChange={(e) => setSenderDraft(e.target.value)} placeholder="mailbox@renvirosystems.com" className="rounded-2xl h-10 flex-1 min-w-[220px]" data-testid="outlook-sender-input" />
              <Button onClick={saveSender} disabled={busy || !senderDraft.trim()} className="rounded-2xl h-10" data-testid="outlook-sender-save">Save</Button>
              <Button variant="ghost" onClick={() => setEditingSender(false)} className="rounded-2xl h-10">Cancel</Button>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs">
                <span className="text-muted-foreground">Sender mailbox:</span>{" "}
                <strong data-testid="outlook-sender-value">{status.sender || "not set"}</strong>
              </p>
              <Button variant="outline" size="sm" className="rounded-xl h-8 text-xs" onClick={() => { setSenderDraft(status.sender || ""); setEditingSender(true); }} data-testid="outlook-sender-edit">
                <Pencil className="h-3 w-3 mr-1" /> Change
              </Button>
            </div>
          )}
          <p className="text-[10px] text-muted-foreground mt-1.5">Must be a real Microsoft 365 mailbox in your tenant. Change it any time — no code edits needed.</p>
        </div>

        {status.connected && (
          <>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <Button onClick={() => setComposeOpen(true)} className="rounded-2xl" data-testid="outlook-compose-btn">
                <Send className="h-4 w-4 mr-1.5" /> Send bulk email
              </Button>
              <div className="flex items-center gap-1.5 flex-1 min-w-[220px]">
                <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="test@company.com" className="rounded-2xl h-10" data-testid="outlook-test-to" />
                <Button variant="outline" onClick={testSend} disabled={busy || !testTo} className="rounded-2xl h-10 shrink-0" data-testid="outlook-test-send">Test send</Button>
              </div>
            </div>
            {testError && (
              <p className="mt-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2" data-testid="outlook-test-error">
                ⚠ {testError}
              </p>
            )}
            {batches.length > 0 && (
              <div className="mt-3 pt-3 border-t border-border/50 space-y-1.5">
                <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-muted-foreground">Recent batches</p>
                {batches.map((b) => {
                  const pending = Math.max(0, (b.total || 0) - (b.sent || 0) - (b.failed || 0));
                  const pct = b.total ? Math.round(((b.sent + b.failed) / b.total) * 100) : 0;
                  const statusLabel = b.status === "queued" ? "Pending" : b.status === "running" ? `Sending… ${pct}%`
                    : b.status === "paused" ? "Paused" : b.status === "interrupted" ? "Paused / Interrupted"
                    : b.status === "cancelled" ? "Stopped" : "Done";
                  const statusCls = b.status === "running" ? "text-amber-600" : b.status === "queued" ? "text-muted-foreground"
                    : (b.status === "paused" || b.status === "interrupted") ? "text-orange-600" : b.status === "cancelled" ? "text-zinc-500"
                    : (b.failed ? "text-red-600" : "text-emerald-700");
                  const canPause = ["running", "queued"].includes(b.status);
                  const canResume = ["paused", "interrupted"].includes(b.status);
                  const canStop = ["running", "queued", "paused", "interrupted"].includes(b.status);
                  const canRetry = b.failed > 0 && b.status !== "running";
                  return (
                  <div key={b.id} data-testid={`outlook-batch-${b.id}`}>
                    <div className="flex items-center justify-between gap-2 text-xs">
                      <span className="truncate flex-1">
                        {b.subject}{b.batch_count > 1 && !String(b.subject).includes("batch") ? ` — Batch ${b.batch_no}/${b.batch_count}` : ""}
                      </span>
                      <span className={`shrink-0 font-semibold ${statusCls}`} data-testid={`outlook-batch-status-${b.id}`}>{statusLabel}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                        <div className={`h-full transition-all ${b.status === "cancelled" ? "bg-zinc-400" : "bg-primary"}`} style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-[10px] text-muted-foreground shrink-0" data-testid={`outlook-batch-stats-${b.id}`}>
                        <span className="text-emerald-700 font-semibold">{b.sent} sent</span>
                        {" · "}<span className={b.failed ? "text-red-600 font-semibold" : ""}>{b.failed} failed</span>
                        {" · "}<span className={pending ? "text-amber-600 font-semibold" : ""}>{pending} pending</span>
                        {" / "}{b.total}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-1 mt-1">
                      {canResume && (
                        <button type="button" onClick={() => batchAction(b, "resume")}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-bold border border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          data-testid={`outlook-batch-resume-${b.id}`}>
                          <Play className="h-2.5 w-2.5" /> Resume
                        </button>
                      )}
                      {canPause && (
                        <button type="button" onClick={() => batchAction(b, "pause")}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-bold border border-amber-300 text-amber-700 hover:bg-amber-50"
                          data-testid={`outlook-batch-pause-${b.id}`}>
                          <Pause className="h-2.5 w-2.5" /> Pause
                        </button>
                      )}
                      {canStop && (
                        <button type="button" onClick={() => batchAction(b, "cancel")}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-bold border border-rose-300 text-rose-700 hover:bg-rose-50"
                          data-testid={`outlook-batch-stop-${b.id}`}>
                          <Square className="h-2.5 w-2.5" /> Stop
                        </button>
                      )}
                      {canRetry && (
                        <button type="button" onClick={() => batchAction(b, "retry-failed")}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-bold border border-sky-300 text-sky-700 hover:bg-sky-50"
                          data-testid={`outlook-batch-retry-${b.id}`}>
                          <RotateCcw className="h-2.5 w-2.5" /> Retry Failed ({b.failed})
                        </button>
                      )}
                      <button type="button" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-bold border border-primary/40 text-primary hover:bg-primary/5"
                        onClick={() => setReportBatch(b)} data-testid={`outlook-batch-report-${b.id}`}>
                        View Report
                      </button>
                    </div>
                    {b.failed > 0 && b.last_error && (
                      <p className="text-[10px] text-red-600 mt-0.5 line-clamp-2" data-testid={`outlook-batch-error-${b.id}`}>
                        Reason: {b.last_error}
                      </p>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
          <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] overflow-y-auto" data-testid="outlook-compose-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">Bulk marketing email</DialogTitle>
              <p className="text-xs text-muted-foreground">Sends from {status.sender}. {Math.max(0, (status.daily_limit || 0) - (status.sent_today || 0))} sends left today.</p>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs font-bold">Subject *</Label>
                <Input className="rounded-2xl h-11 mt-1" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="outlook-subject" />
              </div>
              <div>
                <Label className="text-xs font-bold">Company Logo</Label>
                <input ref={logoRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="outlook-logo-input" />
                <div className="flex items-center gap-3 mt-1" data-testid="outlook-logo-section">
                  {logo?.url ? (
                    <img src={logo.url} alt="Company logo" className="h-12 max-w-[180px] object-contain rounded-lg border border-border bg-white p-1" data-testid="outlook-logo-preview" />
                  ) : (
                    <p className="text-[11px] text-muted-foreground italic" data-testid="outlook-logo-empty">No logo — emails go out without a logo header</p>
                  )}
                  <Button type="button" variant="outline" size="sm" className="rounded-xl h-8 text-xs" disabled={logoBusy} onClick={() => logoRef.current?.click()} data-testid="outlook-logo-upload">
                    <ImagePlus className="h-3.5 w-3.5 mr-1" /> {logoBusy ? "Uploading…" : logo?.url ? "Replace" : "Upload Logo"}
                  </Button>
                  {logo?.url && (
                    <Button type="button" variant="outline" size="sm" className="rounded-xl h-8 text-xs text-red-600 border-red-200" onClick={removeLogo} data-testid="outlook-logo-remove">
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">PNG / JPG / SVG / WebP (auto-converted for inbox safety). Saved once — appears centred at the top of every email, in Preview and in the sent mail, responsive on all devices.</p>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-bold">Message *</Label>
                  <div className="inline-flex p-0.5 rounded-xl bg-muted/60 gap-0.5" data-testid="outlook-msg-tabs">
                    <button type="button" onClick={() => setMsgTab("code")}
                      className={`px-2.5 h-7 rounded-lg text-[11px] font-semibold transition ${msgTab === "code" ? "bg-card shadow-sm border border-border/60" : "text-muted-foreground hover:text-foreground"}`}
                      data-testid="outlook-msg-tab-code">
                      HTML Code
                    </button>
                    <button type="button" onClick={() => setMsgTab("preview")}
                      className={`px-2.5 h-7 rounded-lg text-[11px] font-semibold transition ${msgTab === "preview" ? "bg-card shadow-sm border border-border/60" : "text-muted-foreground hover:text-foreground"}`}
                      data-testid="outlook-msg-tab-preview">
                      Preview
                    </button>
                  </div>
                </div>
                {msgTab === "code" ? (
                  <Textarea className="rounded-2xl mt-1 font-mono text-xs" rows={8} value={form.html} onChange={(e) => setForm({ ...form, html: e.target.value })} placeholder="Write plain text or paste complete HTML email code (header, footer, buttons, images, links)…" data-testid="outlook-body" />
                ) : (
                  <div className="mt-1">
                    <div className="flex items-center justify-center gap-1 mb-2" data-testid="outlook-device-toggle">
                      {[
                        { k: "desktop", label: "Desktop", w: "100%", Icon: Monitor },
                        { k: "laptop", label: "Laptop", w: "720px", Icon: Laptop },
                        { k: "mobile", label: "Mobile", w: "375px", Icon: Smartphone },
                      ].map(({ k, label, Icon }) => (
                        <button key={k} type="button" onClick={() => setPreviewDevice(k)}
                          className={`inline-flex items-center gap-1.5 px-3 h-7 rounded-lg text-[11px] font-semibold border transition ${previewDevice === k ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border/70 text-muted-foreground hover:text-foreground"}`}
                          data-testid={`outlook-device-${k}`}>
                          <Icon className="h-3.5 w-3.5" /> {label}
                        </button>
                      ))}
                    </div>
                    <div className="rounded-2xl border border-border overflow-hidden bg-muted/40 flex justify-center py-2">
                      {form.html.trim() || images.length ? (
                        <iframe
                          title="Email preview"
                          srcDoc={`<!DOCTYPE html><html><head><meta name="viewport" content="width=device-width, initial-scale=1"/></head><body style="margin:0;padding:12px;background:#ffffff">${buildHtml()}</body></html>`}
                          sandbox=""
                          style={{ width: previewDevice === "desktop" ? "100%" : previewDevice === "laptop" ? "720px" : "375px", maxWidth: "100%" }}
                          className={`bg-white border border-border/60 rounded-xl shadow-sm ${previewDevice === "mobile" ? "h-[480px]" : "h-72"}`}
                          data-testid="outlook-preview-frame"
                        />
                      ) : (
                        <p className="text-xs text-muted-foreground text-center py-10" data-testid="outlook-preview-empty">Nothing to preview yet — write or paste your email in the HTML Code tab.</p>
                      )}
                    </div>
                    <p className="text-[10px] text-muted-foreground text-center mt-1">Exactly this HTML is sent to recipients — images auto-fit every screen width.</p>
                  </div>
                )}
                {badImgSrcs.length > 0 && (
                  <div className="mt-1.5 flex items-start gap-2 rounded-2xl bg-amber-50 border border-amber-200 px-3 py-2" data-testid="outlook-bad-img-warning">
                    <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                    <p className="text-[11px] text-amber-800 leading-relaxed">
                      <strong>{badImgSrcs.length} image{badImgSrcs.length === 1 ? "" : "s"} in your HTML won't display in recipient inboxes</strong> (source: {badImgSrcs.slice(0, 2).map((s) => `"${s.slice(0, 40)}${s.length > 40 ? "…" : ""}"`).join(", ")}).
                      Email apps only load images from public <code>https://</code> links. Fix: upload the image below via <strong>Add</strong>, click its <strong>link icon</strong> to copy the hosted URL, and paste that URL into your <code>&lt;img src="…"&gt;</code>.
                    </p>
                  </div>
                )}
                {riskyImgSrcs.length > 0 && (
                  <div className="mt-1.5 flex items-start gap-2 rounded-2xl bg-amber-50 border border-amber-200 px-3 py-2" data-testid="outlook-risky-img-warning">
                    <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                    <p className="text-[11px] text-amber-800 leading-relaxed">
                      <strong>{riskyImgSrcs.length} image{riskyImgSrcs.length === 1 ? "" : "s"} may not display in Outlook</strong> — SVG / WebP files render in the browser preview but Outlook desktop can't show them ({riskyImgSrcs.slice(0, 2).map((s) => `"…${s.slice(-40)}"`).join(", ")}).
                      Fix: upload a <strong>PNG or JPG</strong> version via <strong>Add</strong> below and use its copied URL instead.
                    </p>
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs font-bold">Images (shown inside the email)</Label>
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} data-testid="outlook-image-input" />
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  {images.map((u, i) => (
                    <div key={u} className="relative group" data-testid={`outlook-image-preview-${i}`}>
                      <img src={u} alt="" className="h-16 w-16 object-cover rounded-xl border border-border" />
                      <button type="button" onClick={() => setImages((imgs) => imgs.filter((x) => x !== u))} className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-red-500 text-white grid place-items-center shadow" data-testid={`outlook-image-remove-${i}`}>
                        <X className="h-3 w-3" />
                      </button>
                      <button type="button" onClick={() => copyImageUrl(u)} title="Copy hosted image URL for use inside your HTML"
                        className="absolute -bottom-1.5 -right-1.5 h-5 w-5 rounded-full bg-primary text-primary-foreground grid place-items-center shadow"
                        data-testid={`outlook-image-copy-${i}`}>
                        <LinkIcon className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" className="rounded-xl h-16 w-16 flex-col gap-1 text-[10px]" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="outlook-add-image-btn">
                    <ImagePlus className="h-4 w-4" />
                    {uploading ? "…" : "Add"}
                  </Button>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">JPG / PNG / GIF / WebP, max 5 MB each. Images are hosted publicly so they render in every inbox.</p>
              </div>
              <div>
                <Label className="text-xs font-bold">Recipients * (one per line)</Label>
                <div className="flex items-center gap-2 mt-1.5 mb-1">
                  <span className="text-[11px] font-semibold text-muted-foreground shrink-0">Contact Source:</span>
                  <Select value={contactSource} onValueChange={(v) => { setContactSource(v); setSelCats([]); }}>
                    <SelectTrigger className="rounded-xl h-8 text-xs w-52" data-testid="outlook-contact-source"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-xl">
                      <SelectItem value="marketing" data-testid="outlook-source-marketing">Marketing Contacts</SelectItem>
                      <SelectItem value="crm" data-testid="outlook-source-crm">CRM Contacts</SelectItem>
                    </SelectContent>
                  </Select>
                  <button type="button" className="text-[11px] underline text-primary-700" onClick={loadContactEmails} data-testid="outlook-load-contacts">
                    Load {contactSource === "crm" ? "(CRM Contacts)" : selCats.length ? `(${selCats.join(", ")})` : "all"}
                  </button>
                </div>
                {contactSource === "marketing" ? (
                <div className="flex flex-wrap items-center gap-1 mb-1" data-testid="outlook-cat-picker">
                  <button type="button"
                    onClick={() => setSelCats([])}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${selCats.length === 0 ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border/70 hover:border-primary/50"}`}
                    data-testid="outlook-cat-all">
                    All Marketing Contacts
                  </button>
                  {cats.map((c) => (
                    <button key={c.id} type="button"
                      onClick={() => toggleCat(c.name)}
                      className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${selCats.includes(c.name) ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border/70 hover:border-primary/50"}`}
                      data-testid={`outlook-cat-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      {c.name}
                    </button>
                  ))}
                </div>
                ) : (
                <div className="flex items-center gap-1 mb-1" data-testid="outlook-crm-source-note">
                  <button type="button" disabled={importingCrm} onClick={importCrmToMarketing}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border border-dashed border-blue-400 text-blue-700 hover:bg-blue-50 transition-colors disabled:opacity-50"
                    data-testid="outlook-import-crm">
                    <Copy className="h-3 w-3" /> {importingCrm ? "Importing…" : "Import into Marketing Contacts"}
                  </button>
                  <span className="text-[10px] text-muted-foreground">CRM Contacts only — leads, clients & marketing lists are never mixed in.</span>
                </div>
                )}
                <Textarea className="rounded-2xl mt-1 font-mono text-xs" rows={5} value={form.recipients} onChange={(e) => setForm({ ...form, recipients: e.target.value })} placeholder={"buyer@plant.com\nmaintenance@factory.in"} data-testid="outlook-recipients" />
                <div className="mt-1.5 rounded-2xl border border-border/60 bg-muted/30 px-3 py-2" data-testid="outlook-recipient-stats">
                  <p className="text-[11px] font-semibold flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                    <button type="button" className="underline underline-offset-2 hover:text-primary" onClick={() => setStatDetail("total")} data-testid="outlook-stat-total">Total Recipients: <strong>{recipientStats.total.toLocaleString()}</strong></button>
                    <span className="text-muted-foreground">|</span>
                    <button type="button" className="text-emerald-700 underline underline-offset-2" onClick={() => setStatDetail("unique")} data-testid="outlook-stat-unique">Unique: <strong>{recipientStats.unique.length.toLocaleString()}</strong></button>
                    <span className="text-muted-foreground">|</span>
                    <button type="button" className={`underline underline-offset-2 ${recipientStats.duplicateExtra ? "text-amber-600" : "text-muted-foreground"}`} onClick={() => setStatDetail("duplicates")} data-testid="outlook-stat-duplicates">Duplicates: <strong>{recipientStats.duplicateExtra.toLocaleString()}</strong></button>
                    <span className="text-muted-foreground">|</span>
                    <button type="button" className={`underline underline-offset-2 ${recipientStats.invalid.length ? "text-red-600" : "text-muted-foreground"}`} onClick={() => setStatDetail("invalid")} data-testid="outlook-stat-invalid">Invalid: <strong>{recipientStats.invalid.length.toLocaleString()}</strong></button>
                    <span className="text-muted-foreground">|</span>
                    <span className="text-primary" data-testid="outlook-stat-willsend">Will Send: <strong>{recipientStats.willSend.toLocaleString()}</strong></span>
                  </p>
                  {(recipientStats.duplicateExtra > 0 || recipientStats.invalid.length > 0) && (
                    <p className="text-[10px] text-muted-foreground mt-0.5" data-testid="outlook-stat-skip-note">
                      {[
                        recipientStats.duplicateExtra > 0 ? `${recipientStats.duplicateExtra.toLocaleString()} duplicate${recipientStats.duplicateExtra === 1 ? "" : "s"} will be skipped` : null,
                        recipientStats.invalid.length > 0 ? `${recipientStats.invalid.length.toLocaleString()} invalid email${recipientStats.invalid.length === 1 ? "" : "s"} will be skipped` : null,
                      ].filter(Boolean).join(" · ")}.{" "}
                      {recipientStats.invalid.length > 0 && (
                        <button type="button" className="underline text-red-600 font-semibold" onClick={() => setStatDetail("invalid")} data-testid="outlook-view-invalid-link">View invalid emails →</button>
                      )}
                    </p>
                  )}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setComposeOpen(false)}>Cancel</Button>
              <Button onClick={sendBulk} disabled={busy || !form.subject || !form.html} data-testid="outlook-send-bulk">{busy ? "Starting…" : "Send to all"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* 24.docx — clickable count detail lists */}
        <Dialog open={!!statDetail} onOpenChange={(v) => { if (!v) setStatDetail(null); }}>
          <DialogContent className="rounded-3xl max-w-md" data-testid="outlook-stat-detail-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-lg">
                {statDetail === "total" && `All Recipients (${recipientStats.total.toLocaleString()})`}
                {statDetail === "unique" && `Valid & Unique Emails (${recipientStats.unique.length.toLocaleString()})`}
                {statDetail === "duplicates" && `Duplicate Emails (${recipientStats.duplicates.length.toLocaleString()})`}
                {statDetail === "invalid" && `Invalid Emails (${recipientStats.invalid.length.toLocaleString()})`}
              </DialogTitle>
            </DialogHeader>
            <div className="max-h-72 overflow-y-auto rounded-2xl border border-border/60 divide-y divide-border/40" data-testid="outlook-stat-detail-list">
              {statDetail === "total" && (recipientStats.total === 0
                ? <p className="text-xs text-muted-foreground p-3">No recipients entered yet.</p>
                : recipientStats.entries.slice(0, 1000).map((e, i) => <p key={i} className="px-3 py-1.5 text-xs font-mono truncate">{e}</p>))}
              {statDetail === "unique" && (recipientStats.unique.length === 0
                ? <p className="text-xs text-muted-foreground p-3">No valid, unique emails yet.</p>
                : recipientStats.unique.slice(0, 1000).map((e, i) => <p key={i} className="px-3 py-1.5 text-xs font-mono truncate">{e}</p>))}
              {statDetail === "duplicates" && (recipientStats.duplicates.length === 0
                ? <p className="text-xs text-muted-foreground p-3" data-testid="outlook-no-duplicates">No duplicate emails found.</p>
                : recipientStats.duplicates.map((d, i) => (
                    <div key={i} className="px-3 py-1.5 text-xs flex items-center justify-between gap-2">
                      <span className="font-mono truncate">{d.email}</span>
                      <span className="text-amber-600 font-semibold shrink-0">appears {d.count}×</span>
                    </div>
                  )))}
              {statDetail === "invalid" && (recipientStats.invalid.length === 0
                ? <p className="text-xs text-muted-foreground p-3">No invalid emails found.</p>
                : recipientStats.invalid.map((r, i) => (
                    <div key={i} className="px-3 py-1.5 text-xs flex items-center justify-between gap-2">
                      <span className="font-mono truncate text-red-600">{r.email}</span>
                      <span className="text-muted-foreground shrink-0">{r.reason}</span>
                    </div>
                  )))}
            </div>
            {(statDetail === "total" || statDetail === "unique") && recipientStats[statDetail === "total" ? "entries" : "unique"].length > 1000 && (
              <p className="text-[10px] text-muted-foreground">Showing first 1,000 entries.</p>
            )}
            <div className="flex justify-end">
              <Button variant="outline" className="rounded-2xl" onClick={() => setStatDetail(null)} data-testid="outlook-stat-detail-close">Close</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* 25.docx — full campaign report */}
        <OutlookReportDialog batch={reportBatch} open={!!reportBatch} onOpenChange={(v) => { if (!v) setReportBatch(null); }} />
      </CardContent>
    </Card>
  );
}
